import { useLocation } from 'react-router-dom';
import { NavLink, Link } from '@/lib/nav';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SicaProductLogo, ThemeToggle } from '@sica/ui';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/cn';

const NAV = [
  {
    label: 'Producto',
    href: ROUTES.producto.index,
    submenu: [
      { label: 'Qué es SICA', href: ROUTES.producto.index },
      { label: 'Jurisdicciones', href: ROUTES.producto.jurisdicciones },
      { label: 'Método', href: ROUTES.producto.metodo },
      { label: 'MCP', href: ROUTES.producto.mcp },
    ],
  },
  {
    label: 'Audiencias',
    href: ROUTES.audiencias.docentes,
    submenu: [
      { label: 'Docentes', href: ROUTES.audiencias.docentes },
      { label: 'Directivos', href: ROUTES.audiencias.directivos },
      { label: 'Especialistas', href: ROUTES.audiencias.especialistas },
      { label: 'Desarrolladores', href: ROUTES.audiencias.desarrolladores },
    ],
  },
  { label: 'Glosario', href: ROUTES.glosario },
  { label: 'Sobre', href: ROUTES.sobre },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-paper/85 backdrop-blur-sm">
      <a href="#main" className="skip-link">
        Saltar al contenido
      </a>
      <div className="container flex h-16 items-center justify-between">
        <Link to={ROUTES.home} className="focus-ring -ml-1 rounded px-1 py-1" aria-label="RVD.AI SICA, inicio">
          <SicaProductLogo className="h-auto w-40 sm:w-44" decorative />
        </Link>

        <nav aria-label="Principal" className="hidden items-center gap-1 md:flex">
          {NAV.map((item) =>
            item.submenu ? (
              <NavGroup key={item.label} label={item.label} submenu={item.submenu} />
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'focus-ring rounded px-3 py-2 text-sm transition-colors',
                    isActive ? 'text-ink font-medium' : 'text-ink-muted hover:text-ink',
                  )
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
          <ThemeToggle className="ml-2" />
          <Link
            to={ROUTES.contacto}
            className="focus-ring ml-1 rounded-full border border-line_strong px-4 py-2 text-sm font-medium text-ink transition-colors hover:border-ink hover:bg-ink hover:text-paper"
          >
            Hablar con nosotros
          </Link>
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            className="focus-ring -mr-2 rounded p-2"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Móvil"
          className="border-t border-line bg-paper md:hidden"
        >
          <div className="container py-4">
            <ul className="flex flex-col gap-1">
              {NAV.flatMap((item) =>
                item.submenu
                  ? [
                      <li key={item.label} className="pt-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
                        {item.label}
                      </li>,
                      ...item.submenu.map((sub) => (
                        <li key={sub.href}>
                          <NavLink
                            to={sub.href}
                            className={({ isActive }) =>
                              cn(
                                'focus-ring block rounded px-2 py-2 text-sm',
                                isActive ? 'text-ink font-medium' : 'text-ink-muted',
                              )
                            }
                          >
                            {sub.label}
                          </NavLink>
                        </li>
                      )),
                    ]
                  : [
                      <li key={item.href}>
                        <NavLink
                          to={item.href}
                          className={({ isActive }) =>
                            cn(
                              'focus-ring block rounded px-2 py-2 text-sm',
                              isActive ? 'text-ink font-medium' : 'text-ink-muted',
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      </li>,
                    ],
              )}
              <li className="mt-3">
                <Link
                  to={ROUTES.contacto}
                  className="focus-ring block rounded-full border border-line_strong px-4 py-2 text-center text-sm font-medium text-ink"
                >
                  Hablar con nosotros
                </Link>
              </li>
            </ul>
          </div>
        </nav>
      )}
    </header>
  );
}

function NavGroup({
  label,
  submenu,
}: {
  label: string;
  submenu: Array<{ label: string; href: string }>;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        className="focus-ring rounded px-3 py-2 text-sm text-ink-muted transition-colors hover:text-ink"
        aria-haspopup="true"
      >
        {label}
      </button>
      <div
        role="menu"
        className="invisible absolute left-0 top-full mt-1 min-w-[200px] rounded-md border border-line bg-paper py-2 opacity-0 shadow-lg transition-[opacity,visibility] duration-150 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
      >
        {submenu.map((sub) => (
          <NavLink
            key={sub.href}
            to={sub.href}
            role="menuitem"
            className={({ isActive }) =>
              cn(
                'focus-ring block px-4 py-2 text-sm transition-colors',
                isActive ? 'text-ink font-medium bg-paper-tinted' : 'text-ink-muted hover:bg-paper-tinted hover:text-ink',
              )
            }
          >
            {sub.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
